import {Driver} from 'app/blocks/model/driver.model';
import {AbstractEntityResource} from 'app/blocks/resource/abstract-entity-resource';
import {ListViewParams} from 'app/blocks/util/list-view-params';

import {DEFAULT_ENTITY_NAMED_LIST} from 'app/app.constants';
import {EntityView} from 'app/blocks/util/entity-view';

export interface ActionMeta {
    currentPage?: string;
}

export interface IAction {
    suppressMessages?: boolean;
}

export interface IEntityAction extends IAction {
    resource: AbstractEntityResource<any>;
    currentPage?: EntityView;
}

export interface IUpdateEntity extends IEntityAction {
    entity: any;
}

export interface IStartInlineEntityUpdate extends IEntityAction {
    id: number;
}

export type IStartInlineEntityAdd = IEntityAction;

export interface ICancelInlineEntityUpdate extends IEntityAction {
    id?: number;
}

export interface IUpdateInlineEntity extends IEntityAction {
    entity: any;
}

export interface ISelectEntity extends IEntityAction {
    entityIds: number[];
    entityName?: string;
}
export interface ISelectAllCurrentPageEntities extends IEntityAction {
    pageIndex?: number;
    entityName?: string;
}

export interface IUnselectEntity extends IEntityAction {
    entityIds: number[];
    entityName?: string;
}

export interface IUnselectAllCurrentPageEntities extends IEntityAction {
    pageIndex?: number;
    entityName?: string;
}

export interface ISelectEntityTabIndex extends IEntityAction {
    tabIndex: number;
}

export interface IGetEntity extends IEntityAction {
    id: number;
}

export interface IGetNewEntity extends IEntityAction {
    // ID is not required, but tslint won't allow us to have empty interfaces
    id?: number;
}

export interface IGetEntityList extends IEntityAction {
    viewParams?: ListViewParams;
    primaryFilter?: string;
    entityName?: string; // for when we want multiple store instances for the same entity
}

export interface IChangeEntityListViewParams extends IEntityAction {
    viewParams?: ListViewParams;
    primaryFilter?: string;
    resourceName?: string;
}

export interface ICreateEntity extends IEntityAction {
    entity: any;
}

export interface ICreateInlineEntity extends IEntityAction {
    entity: any;
}

export interface IDeleteEntity extends IEntityAction {
    id: number;
    req?: any;
}

export interface IDeleteInlineEntity extends IEntityAction {
    id: number;
}

export interface IGetNamedEntityList extends IEntityAction {
    name?: string;
}

export interface IGetNewEntityWithSettings extends IEntityAction {
    settingResourceName?: string;
}

export interface IExportEntities extends IEntityAction {
    ids: Set<number>;
    type: string;
    filename: string;
    params: any;
}

export interface IForceGetEntityList extends IEntityAction {
    pageIndex?: number;
    primaryFilter?: string;
    entityName?: string;
}

export interface IPopulateEntity extends IEntityAction {
    entity: any;
}

export interface IPopulateEntityList extends IEntityAction {
    list: any[];
}

export interface IReplaceEntityListItem extends IEntityAction {
    entity: any;
}

export interface IInsertEntityListItem extends IEntityAction {
    entity: any;
    index: number;
}

export class AbstractGetNamedEntityList implements IEntityAction {
    constructor(
        public resource: AbstractEntityResource<any>,
        public name: string = DEFAULT_ENTITY_NAMED_LIST,
        public forceReload: boolean = false,
        public meta?: ActionMeta,
        public pageSize?: number
    ) {}
}

export interface IDuplicateEntity extends IEntityAction {
    id: number;
}

export interface IGetOutstandingCallIns extends IAction {
    filters?: any;
    onlyIfShown?: boolean;
}

// New Entity Actions

export class UpdateEntity implements IUpdateEntity {
    static readonly type = '[Generic] Update Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public entity: any,
        public meta?: ActionMeta
    ) {}
}

export class UpdateInlineEntity implements IUpdateInlineEntity {
    static readonly type = '[Generic] Update Inline Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public entity: any,
        public meta?: ActionMeta
    ) {}
}

export class StartInlineEntityUpdate implements IStartInlineEntityUpdate {
    static readonly type = '[Generic] Start Update of Inline Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public id: number,
        public meta?: ActionMeta
    ) {}
}

export class StartInlineEntityAdd implements IStartInlineEntityAdd {
    static readonly type = '[Generic] Start Addition of Inline Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public meta?: ActionMeta
    ) {}
}

export class CancelInlineEntityUpdate implements ICancelInlineEntityUpdate {
    static readonly type = '[Generic] Cancel Update of Inline Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public id?: number,
        public meta?: ActionMeta
    ) {}
}

export class SelectEntity implements ISelectEntity {
    static readonly type = '[Generic] Select Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public entityIds: number[],
        public meta?: ActionMeta,
        public entityName?: string
    ) {}
}

export class SelectAllCurrentPageEntities implements ISelectAllCurrentPageEntities {
    static readonly type = '[Generic] Select All Current Page Entities';
    constructor(
        public resource: AbstractEntityResource<any>,
        public pageIndex?: number,
        public meta?: ActionMeta,
        public entityName?: string
    ) {}
}

export class UnselectEntity implements IUnselectEntity {
    static readonly type = '[Generic] Unselect Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public entityIds: number[],
        public meta?: ActionMeta,
        public entityName?: string
    ) {}
}

export class UnselectAllCurrentPageEntities implements IUnselectAllCurrentPageEntities {
    static readonly type = '[Generic] Unselect all Current Page Entities';
    constructor(
        public resource: AbstractEntityResource<any>,
        public pageIndex?: number,
        public meta?: ActionMeta,
        public entityName?: string
    ) {}
}

export class SelectEntityTabIndex implements ISelectEntityTabIndex {
    static readonly type = '[Generic] Select Entity Tab Index';
    constructor(
        public resource: AbstractEntityResource<any>,
        public tabIndex: number,
        public meta?: ActionMeta
    ) {}
}

export class GetEntity implements IGetEntity {
    static readonly type = '[Generic] Get Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public id: number,
        public meta?: ActionMeta
    ) {}
}

export class GetNewEntity implements IGetNewEntity {
    static readonly type = '[Generic] Get New Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public id?: number,
        public meta?: ActionMeta
    ) {}
}

export class GetEntityList implements IGetEntityList {
    static readonly type = '[Generic] Get Entity List';
    // constructor(public resource: AbstractEntityResource<any>, public meta?: ActionMeta){}
    constructor(
        public resource: AbstractEntityResource<any>,
        public meta?: ActionMeta,
        public viewParams?: ListViewParams,
        public primaryFilter?: string,
        public entityName?: string // for when we want multiple store instances for the same entity
    ) {}
}

export class ChangeEntityListViewParams implements IChangeEntityListViewParams {
    static readonly type = '[Generic] Change Entity List View Params';
    constructor(
        public resource: AbstractEntityResource<any>,
        public viewParams?: ListViewParams,
        public meta?: ActionMeta,
        public primaryFilter?: string,
        public entityName?: string
    ) {}
}

export class ChangeEntityFilterParams {
    static readonly type = '[Generic] Change Entity List filter Params only';
    constructor(
        public entityName: string,
        public filterParams?: any
    ) {}
}

export class CreateEntity implements ICreateEntity {
    static readonly type = '[Generic] Create Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public entity: any,
        public meta?: ActionMeta
    ) {}
}

export class CreateInlineEntity implements ICreateInlineEntity {
    static readonly type = '[Generic] Create Inline Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public entity: any,
        public meta?: ActionMeta
    ) {}
}

export class DeleteEntity implements IDeleteEntity {
    static readonly type = '[Generic] Delete Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public id: number,
        public meta?: ActionMeta,
        public req?: any
    ) {}
}

export class DeleteInlineEntity implements IDeleteInlineEntity {
    static readonly type = '[Generic] Delete Inline Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public id: number,
        public meta?: ActionMeta
    ) {}
}

export class GetNamedEntityList extends AbstractGetNamedEntityList {
    static readonly type = '[Generic] Get Named Entity List';
}

export class GetNewEntityWithSettings implements IGetNewEntityWithSettings {
    static readonly type = '[Generic] Get New Entity With Settings';
    constructor(
        public resource: AbstractEntityResource<any>,
        public settingResourceName,
        public id?: number,
        public meta?: ActionMeta
    ) {}
}

export class ExportEntities implements IExportEntities {
    static readonly type = '[Generic] Export Entities';
    constructor(
        public resource: AbstractEntityResource<any>,
        public ids: Set<number>,
        public type: string,
        public filename: string,
        public params: any,
        public meta?: ActionMeta
    ) {}
}

export class DuplicateEntity implements IDuplicateEntity {
    static readonly type = '[Generic] Duplicate Entity and Update Create / Edit';
    constructor(
        public resource: AbstractEntityResource<any>,
        public id: number
    ) {}
}

export class ForceGetEntityList implements IForceGetEntityList {
    static readonly type = '[Generic] Force Get Entity List';
    constructor(
        public resource: AbstractEntityResource<any>,
        public pageIndex?: number,
        public meta?: ActionMeta,
        public primaryFilter?: string,
        public entityName?: string
    ) {}
}

export class PopulateEntity implements IPopulateEntity {
    static readonly type = '[Generic] Populate Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public entity: any,
        public meta?: ActionMeta
    ) {}
}

export class PopulateEntityList implements IPopulateEntityList {
    static readonly type = '[Generic] Populate Entity List';
    constructor(
        public resource: AbstractEntityResource<any>,
        public list: any[],
        public meta?: ActionMeta
    ) {}
}

export class ReplaceEntityListItem implements IReplaceEntityListItem {
    static readonly type = '[Generic] Replace Entity List Item';
    constructor(
        public resource: AbstractEntityResource<any>,
        public entity: any,
        public meta?: ActionMeta
    ) {}
}

export class InsertEntityListItem implements IInsertEntityListItem {
    static readonly type = '[Generic] Insert Entity List Item';
    constructor(
        public resource: AbstractEntityResource<any>,
        public entity: any,
        public index: number,
        public meta?: ActionMeta
    ) {}
}

export interface IActivateResource extends IEntityAction {
    id: number;
}

export class ActivateResource implements IActivateResource {
    static readonly type = '[Generic] Restore Entity';
    constructor(
        public resource: AbstractEntityResource<any>,
        public id: number,
        public meta?: ActionMeta
    ) {}
}

export class GetOutstandingCallIns implements IGetOutstandingCallIns {
    static readonly type = '[Outstanding Call-Ins] Get Outstanding Call-Ins';
    /**
     * @param onlyIfShown background refresh (e.g. after a call-in is saved): skipped unless a screen showing
     * the outstanding call-ins is open, since nothing else reads this state.
     */
    constructor(
        public filters?: any,
        public onlyIfShown?: boolean
    ) {}
}

/** A screen showing the outstanding call-ins state opened (true) or closed (false). */
export class SetOutstandingCallInsShown {
    static readonly type = '[Outstanding Call-Ins] Set Shown';
    constructor(public shown: boolean) {}
}

export const actionTypes = [
    GetEntity,
    GetEntityList,
    CreateEntity,
    UpdateEntity,
    UpdateInlineEntity,
    SelectEntity,
    UnselectEntity,
    SelectAllCurrentPageEntities,
    UnselectAllCurrentPageEntities,
    SelectEntityTabIndex,
    DeleteEntity,
    DeleteInlineEntity,
    GetNamedEntityList,
    GetNewEntity,
    ChangeEntityListViewParams,
    ExportEntities,
    DuplicateEntity,
    ForceGetEntityList,
    PopulateEntity,
    PopulateEntityList,
    ReplaceEntityListItem,
    InsertEntityListItem
];
